// ledger.tsx — the RESTRUCTURED Habits home, ported from the Claude Design delivery
// "The Punch Ledger" (project 3ed5f0ef).
//
// The a3 restructure this implements (DESIGN.md, VERDICT FAIL):
//   R1 the home LEADS WITH THE RECORD OF DAYS — a board of the last week across ALL habits.
//      Every leader in this category leads with the record (HabitKit's tile grid, Way of Life's
//      colour board); we led with our own app name and an AI panel.
//   R2 TODAY IS MARKABLE IN ONE GESTURE from the home, per habit, without opening anything.
//      Speed of entry is the product here — Way of Life sells "7-8 areas in a few seconds".
//   R3 THREE STATES: done / missed / skipped. `missed` is the ABSENCE of a row on a past day;
//      only done and skipped are stored (see the db.rs migration). A deliberate rest day must
//      never render as failure.
//   R5 per-habit colour is used properly — but the board is ALSO readable without colour:
//      each habit carries a distinct SHAPE (circle/square/diamond/triangle), because eight
//      habit colours side by side is a real accessibility trap.
import React, { useMemo, useState } from "react";
import type { Habit, Entry, DayState } from "./store";
import { store } from "./store";
import { DayMark, CadenceMark, type CadenceId } from "./marks";

const DAY = 86_400_000;
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const todayISO = () => iso(new Date());

/** The last `n` days, oldest first, ending today. */
/** The n days ending `weeksBack` weeks ago. weeksBack=0 is the current week. */
export function lastDays(n: number, weeksBack = 0): string[] {
  const out: string[] = [];
  const end = Date.now() - weeksBack * 7 * DAY;
  for (let i = n - 1; i >= 0; i--) out.push(iso(new Date(end - i * DAY)));
  return out;
}

/** Colour is not enough — eight habits side by side need a shape too. */
const SHAPES = ["circle", "square", "diamond", "triangle"] as const;
export const shapeFor = (i: number) => SHAPES[i % SHAPES.length];

const DOW = ["S", "M", "T", "W", "T", "F", "S"];

/** How each state is SAID out loud. Screen-reader users get the meaning, not the enum name. */
const SAY: Record<CellState, string> = {
  done: "done", skipped: "rest day", missed: "missed", unmarked: "not marked",
  slipped: "slipped", clean: "stayed clean",
};

/** What a given day is, for a given habit. A day with no row is `missed` only once it is in the
 *  PAST — today is simply unmarked, and the future is nothing at all. */
export type CellState = DayState | "missed" | "unmarked" | "slipped" | "clean";
/**
 * Does this habit's schedule EXPECT a mark on this day?
 *
 * 🔴 This is why the function exists. cellState used to call every past unmarked day "missed"
 * regardless of cadence, so a WEEKLY habit showed six missed circles a week — telling the customer
 * they failed on days they were never meant to act. Wrong, and the most demoralising possible way
 * to be wrong in a habit app.
 *
 * "count:N" deliberately never yields an expected day: you cannot say WHICH day of a "3× a week"
 * habit was missed, only that the week fell short. Claiming otherwise would invent a failure.
 */
export function expectsDay(schedule: string, day: string): boolean {
  const s = (schedule || "daily").trim();
  if (s === "adhoc" || s.startsWith("count:")) return false;
  if (s.startsWith("days:")) {
    const want = s.slice(5).split(",").map((n) => Number(n.trim())).filter((n) => !Number.isNaN(n));
    // Parse as a LOCAL date — new Date("YYYY-MM-DD") is UTC midnight and shifts the weekday west of Greenwich.
    const [y, m, d] = day.slice(0, 10).split("-").map(Number);
    return want.includes(new Date(y, m - 1, d).getDay());
  }
  return true; // "daily"
}

export function cellState(
  entries: Entry[],
  day: string,
  today: string,
  schedule = "daily",
  polarity = "build",
  createdAt?: string,
): CellState {
  const e = entries.find((x) => x.logged_on.slice(0, 10) === day);

  // A habit you are trying to BREAK inverts every meaning on this board. A mark is a SLIP, and an
  // empty past day is the achievement — the customer stayed clean. Rendering that as "missed" (as
  // the build path does) told someone quitting late-night scrolling that they had failed seven days
  // running, when in fact they had succeeded seven days running. Caught live 2026-08-13.
  if (polarity === "break") {
    if (e) return "slipped";
    if (day >= today) return "unmarked";                    // the day is not over yet
    if (createdAt && day < createdAt.slice(0, 10)) return "unmarked";  // before they started
    return "clean";
  }

  if (e) return e.state === "skipped" ? "skipped" : "done";
  if (day >= today) return "unmarked";
  return expectsDay(schedule, day) ? "missed" : "unmarked";
}

/** THE BOARD — the home's lead. Habits down, the last 7 days across, one tap to mark. */
export function Board({ habits, entriesByHabit, onChanged }: {
  habits: Habit[];
  entriesByHabit: Record<string, Entry[]>;
  onChanged: () => Promise<void> | void;
}) {
  const today = todayISO();
  // Paging back is what makes a missed day FIXABLE — HabitKit sells exactly this ("tap a day to
  // remove or add a completion") and Way of Life sells the long view ("weeks, months or even
  // years"). A 7-day-only board silently makes last Sunday uncorrectable.
  const [weeksBack, setWeeksBack] = useState(0);
  const days = useMemo(() => lastDays(7, weeksBack), [weeksBack]);
  const [busy, setBusy] = useState<string | null>(null);
  const [justLogged, setJustLogged] = useState<string | null>(null);

  /** ONE GESTURE (R2): tap cycles done → skipped → clear. A rest day is a deliberate mark, not
   *  an absence, so it takes exactly one more tap than doing the thing. */
  async function cycle(habit: Habit, day: string, current: CellState) {
    const key = `${habit.id}:${day}`;
    if (busy) return;
    setBusy(key);
    try {
      // A break habit has no "rest day" — you either slipped or you didn't. So its gesture is a
      // plain toggle, not the three-step build cycle.
      if (habit.polarity === "break") {
        if (current === "slipped") {
          const e = (entriesByHabit[habit.id] ?? []).find((x) => x.logged_on.slice(0, 10) === day);
          if (e) await store.deleteEntry(e.id);
        } else {
          await store.logEntry(habit.id, day, null, "done");
        }
      } else if (current === "done") {
        await store.logEntry(habit.id, day, null, "skipped");
      } else if (current === "skipped") {
        const e = (entriesByHabit[habit.id] ?? []).find((x) => x.logged_on.slice(0, 10) === day);
        if (e) await store.deleteEntry(e.id);
      } else {
        await store.logEntry(habit.id, day, null, "done");
        setJustLogged(key);
        setTimeout(() => setJustLogged((k) => (k === key ? null : k)), 400);
      }
      await onChanged();
    } finally { setBusy(null); }
  }

  if (!habits.length) return null;

  return (
    <section className="card board" aria-label="Your record of days">
      {/* Week nav — plain prose, not chrome. The board is the product; this is the smallest thing
          that makes an older day reachable and correctable. */}
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.35rem" }}>
        <button type="button" className="btn btn-ghost" style={{ fontSize: "var(--fs-xs)", padding: "0.2rem 0.5rem" }}
          onClick={() => setWeeksBack((w) => w + 1)} aria-label="Earlier week">← earlier</button>
        <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>
          {weeksBack === 0 ? "This week" : weeksBack === 1 ? "Last week" : `${weeksBack} weeks ago`}
        </span>
        <button type="button" className="btn btn-ghost"
          style={{ fontSize: "var(--fs-xs)", padding: "0.2rem 0.5rem", visibility: weeksBack === 0 ? "hidden" : "visible" }}
          onClick={() => setWeeksBack((w) => Math.max(0, w - 1))} aria-label="Later week">later →</button>
      </div>
      <div className="board-head">
        <span className="board-corner" />
        {days.map((d) => {
          const dt = new Date(d + "T00:00:00");
          return (
            <span key={d} className={`board-daylabel${d === today ? " today" : ""}`}>
              {DOW[dt.getDay()]}
            </span>
          );
        })}
      </div>

      {habits.map((h, i) => {
        const entries = entriesByHabit[h.id] ?? [];
        const streak = countStreakFor(entries, today, h.polarity, h.created_at);
        return (
          <div className="board-row" key={h.id} style={{ ["--hab-color" as string]: h.color }}>
            <span className="board-habit">
              <span className="habit-chip" data-shape={shapeFor(i)} aria-hidden="true" />
              <CadenceMark cadence={(h.cadence_target as CadenceId) ?? "daily"} />
              <span className="board-habit-name">{h.name}</span>
              <span className="streak-chip tnum">{streak > 0 ? `${streak}d` : "—"}</span>
            </span>
            <span className="board-cells">
            {days.map((d) => {
              const st = cellState(entries, d, today, h.schedule, h.polarity, h.created_at);
              const key = `${h.id}:${d}`;
              const paint = st === "clean" ? "done" : st === "slipped" ? "missed" : st;
              const cls = ["day-cell", paint === "unmarked" ? "" : paint, d === today ? "today" : "",
                           justLogged === key ? "just-logged" : ""].filter(Boolean).join(" ");
              return (
                <button key={d} className={cls} disabled={d > today || busy === key}
                        onClick={() => cycle(h, d, st)}
                        aria-label={`${h.name}, ${d}: ${SAY[st]}. Tap to change.`}>
                  {paint === "done" || paint === "skipped" || paint === "missed"
                    ? <DayMark state={paint === "missed" ? "missed" : (paint as DayState)} />
                    : null}
                </button>
              );
            })}
            </span>
          </div>
        );
      })}
    </section>
  );
}

/** Consecutive days up to today, with a one-day grace. A SKIPPED day does not break a streak —
 *  that is the whole point of having a third state. */
/**
 * Consecutive days, counted back from today.
 *
 * For a "break" habit the meaning INVERTS: a mark means "I slipped", so the streak is the run of
 * days with NO mark. Counting marks there would celebrate the exact thing the customer is trying
 * to stop — the kind of inversion that makes an app feel like it isn't listening.
 */
export function countStreakFor(
  entries: Entry[],
  today: string,
  polarity = "build",
  createdAt?: string,
): number {
  if (polarity !== "break") return countStreak(entries, today);
  const marked = new Set(entries.map((e) => e.logged_on.slice(0, 10)));
  // A clean run can only start when the customer STARTED TRACKING. Without this floor the count
  // walks back through every day that ever existed and a habit added this morning reports a
  // ten-year streak (seen live, 2026-08-13: "3651d" on a habit thirty seconds old).
  const from = (createdAt || today).slice(0, 10);
  let n = 0;
  for (let i = 0; ; i++) {
    const d = iso(new Date(Date.parse(today + "T00:00:00") - i * DAY));
    if (d < from || marked.has(d)) break;
    n++;
  }
  return n;
}

export function countStreak(entries: Entry[], today: string): number {
  const byDay = new Map(entries.map((e) => [e.logged_on.slice(0, 10), e.state]));
  let cur = today;
  if (!byDay.has(cur)) {
    const d = new Date(cur + "T00:00:00"); d.setDate(d.getDate() - 1); cur = iso(d);
    if (!byDay.has(cur)) return 0;
  }
  let n = 0;
  while (byDay.has(cur)) {
    // Anything that is not explicitly "skipped" counts as done. Rows written before the `state`
    // column existed have no state at all, and a strict `=== "done"` made every historical
    // streak read as zero — the board showed "—" while the habit rows below said "5 days".
    if (byDay.get(cur) !== "skipped") n++;   // skipped days bridge the run without counting
    const d = new Date(cur + "T00:00:00"); d.setDate(d.getDate() - 1); cur = iso(d);
  }
  return n;
}

/**
 * THE RECOVERY RECORD — what the app can say at the moment people quit.
 *
 * A broken streak is the abandonment event in this category. Every competitor's answer is to reset
 * a number to zero, which is accurate and useless: it throws away the fact that the customer has
 * been here before and come back. That fact is already sitting in their own data.
 *
 * Returns null when there is nothing TRUE to say — one run, or none. The sentence has to be earned
 * from the record, never manufactured; "you've got this!" is the kind of empty encouragement this
 * is meant to replace.
 */
export interface Recovery {
  longest: number;
  restarts: number;
  /** Each fresh start has outlasted the one before it — only claimed when it is literally true. */
  improving: boolean;
}

export function recoveryRecord(entries: Entry[], today?: string, polarity = "build"): Recovery | null {
  // "You've started again N times" is a BUILD concept. On a break habit the entries are SLIPS, so
  // this would compute the longest run of SLIPPING and present it as a personal best — caught in a
  // screenshot, reading "Late-night scrolling · Longest run 1 day. You've started again once."
  // The break equivalent is cleanRecord below, which measures the gaps instead of the marks.
  if (polarity === "break") return null;
  const days = entries
    .filter((e) => e.state !== "skipped")
    .map((e) => e.logged_on.slice(0, 10))
    .sort();
  if (days.length === 0) return null;

  // Consecutive calendar days form a run. A gap of any length ends it.
  const runs: number[] = [];
  let len = 0;
  let prev: string | null = null;
  for (const d of days) {
    if (prev !== null && Date.parse(d) - Date.parse(prev) === DAY) len++;
    else { if (len) runs.push(len); len = 1; }
    prev = d;
  }
  if (len) runs.push(len);

  if (runs.length < 2) return null;                    // never restarted — nothing to say yet
  // The final run counts as COMPLETED unless it is still live (ends today or yesterday). Treating
  // a run that stopped three weeks ago as "ongoing" quietly hid it from the comparison, so a
  // customer with runs of 2 then 4 was told nothing rather than that they are getting better.
  const last = days[days.length - 1];
  const live = today
    ? Date.parse(today + "T00:00:00") - Date.parse(last + "T00:00:00") <= DAY
    : false;
  const completed = live ? runs.slice(0, -1) : runs;
  const improving = completed.length >= 2 && completed.every((r, i) => i === 0 || r > completed[i - 1]);
  return { longest: Math.max(...runs), restarts: runs.length - 1, improving };
}

/** The sentence, or null. Kept next to the arithmetic so the wording cannot drift from the maths. */
export function recoveryLine(r: Recovery | null): string | null {
  if (!r) return null;
  const times = r.restarts === 1 ? "once" : `${r.restarts} times`;
  const tail = r.improving ? " — and each one has lasted longer than the last" : "";
  return `Longest run ${r.longest} day${r.longest === 1 ? "" : "s"}. You've started again ${times}${tail}.`;
}

/**
 * THE BREAK-HABIT EQUIVALENT — the longest stretch they stayed clean.
 *
 * Mirrors recoveryRecord, but measures the GAPS between marks rather than the marks themselves,
 * because on a habit you are quitting the marks are the failures. Counted from the day they started
 * tracking, so a habit added this morning cannot claim a decade.
 */
export interface CleanRecord {
  longest: number;
  slips: number;
}

export function cleanRecord(entries: Entry[], today: string, createdAt?: string): CleanRecord | null {
  const slips = new Set(entries.map((e) => e.logged_on.slice(0, 10)));
  if (slips.size === 0) return null;                 // never slipped — nothing to reassure them about
  const sorted = [...slips].sort();
  const start = createdAt ? createdAt.slice(0, 10) : sorted[0];
  let longest = 0;
  let run = 0;
  for (let t = Date.parse(start + "T00:00:00"); t <= Date.parse(today + "T00:00:00"); t += DAY) {
    const d = iso(new Date(t));
    if (slips.has(d)) { longest = Math.max(longest, run); run = 0; } else { run++; }
  }
  return { longest: Math.max(longest, run), slips: slips.size };
}

export function cleanLine(r: CleanRecord | null): string | null {
  if (!r || r.longest < 1) return null;
  const times = r.slips === 1 ? "once" : `${r.slips} times`;
  return `Longest clean stretch ${r.longest} day${r.longest === 1 ? "" : "s"}. You've slipped ${times} since you started.`;
}
